import AlertTriangleFilledIcon from '@elementor/icons/AlertTriangleFilledIcon';
import ChevronDownSmallIcon from '@elementor/icons/ChevronDownSmallIcon';
import Box from '@elementor/ui/Box';
import Typography from '@elementor/ui/Typography';
import { memo, useMemo } from 'react';
import { useScannerWizardContext } from '@context/scanner-wizard-context';
import ColorContrastPreview from '@layouts/color-contrast-layout/components/color-contrast-preview';
import {
	StyledAccordion,
	StyledAccordionSummary,
} from '@styles/manual-fixes.styles';
import { rgbOrRgbaToHex } from '@utils/color-contrast-helpers';
import { __ } from '@utils/i18n';
import { queryIframeWindow } from '@utils/query';
import ColorContrastForm from './components/color-contrast-form';

/**
 * Memoized accordion item to prevent re-renders when other items change.
 * Only re-renders when its specific data changes.
 */
const ColorContrastAccordionItem = memo(
	({
		item,
		index,
		isExpanded,
		onToggle,
		formData,
		onUpdateFormData,
	}) => {
		const nodeText = item.node.innerText.trim();
		const title = nodeText ? nodeText : __('Insufficient contrast');

		const color =
			item.messageArgs[3] ||
			rgbOrRgbaToHex(
				queryIframeWindow()
					.getComputedStyle(item.node)
					.getPropertyValue('color'),
			);
		const background = item.messageArgs[4];

		return (
			<StyledAccordion
				elevation={0}
				square
				disableGutters
				expanded={isExpanded}
				onChange={onToggle}
			>
				<StyledAccordionSummary
					expandIcon={<ChevronDownSmallIcon />}
					aria-controls={`manual-panel-${index}`}
					id={`manual-panel-${index}`}
				>
					<AlertTriangleFilledIcon color="error" fontSize="small" />

					<ColorContrastPreview color={color} backgroundColor={background} />

					<Typography variant="body2" as="h4" noWrap>
						{title}
					</Typography>
				</StyledAccordionSummary>

				<ColorContrastForm
					item={item}
					formData={formData}
					onUpdateFormData={onUpdateFormData}
				/>
			</StyledAccordion>
		);
	},
);

ColorContrastAccordionItem.displayName = 'ColorContrastAccordionItem';

const ColorContrastLayout = () => {
	const {
		openIndex,
		handleOpen,
		openedBlock,
		sortedViolations,
		colorContrastData,
		setColorContrastData,
	} = useScannerWizardContext();

	const items = sortedViolations[openedBlock];

	// Memoize toggle handlers to prevent new functions on each render
	const toggleHandlers = useMemo(() => {
		return items.map((item, index) => handleOpen(index, item));
	}, [items, handleOpen]);

	// Memoize update handlers per path to prevent new functions on each render
	const updateHandlers = useMemo(() => {
		const handlers = {};
		items.forEach((item) => {
			const path = item.path.dom;
			handlers[path] = (data) => {
				setColorContrastData((prev) => ({
					...prev,
					[path]: {
						...(prev[path] || {}),
						...data,
					},
				}));
			};
		});
		return handlers;
	}, [items, setColorContrastData]);

	return (
		<Box sx={{ pb: 8 }}>
			{items.map((item, index) => {
				const path = item.path.dom;
				const formData = colorContrastData[path] || null;

				return (
					<ColorContrastAccordionItem
						key={`${item.ruleId}-${index}`}
						item={item}
						index={index}
						isExpanded={openIndex === index}
						onToggle={toggleHandlers[index]}
						formData={formData}
						onUpdateFormData={updateHandlers[path]}
					/>
				);
			})}
		</Box>
	);
};

export default ColorContrastLayout;
